import { Product, UnboundProductData, UnboundProductOptionData } from "@/products/product.base";
import type { ProductSelector, Store } from "@/store";
import { STORE_ADAPTER, STORE_CAMPAIGN, STORE_OVERRIDES, STORE_TRANSFORMERS, STORE_TRIAL_LINKS } from "@/store.constants";

export type ProviderData = { store: Store };
export const INGNORE_CAMPAIGN = ["ignore", "none", "0"];
export type FetchedProduct = {
  overrides: {
    oldCampaign?: string,
    newCampaign?: string
  },
  product: UnboundProductData | undefined
};

export abstract class Provider {
  protected store: Store;

  constructor({ store }: ProviderData) {
    this.store = store;
  }

  async getProduct(param: ProductSelector): Promise<Product | undefined> {
    let data = await this.fetch(param);

    if (!data) {
      return undefined;
    }
    data = await this.applyOverrides(data);
    data = await this.applyTrialLinks(data);
    data = await this.applyTransformers(data);

    return this._getProduct(data.product);
  }

  protected abstract _getProduct(product: UnboundProductData | undefined): Product | undefined;

  private async applyOverrides(data: FetchedProduct) {
    const { overrides, product } = data;

    if (!product) {
      return data;
    }

    const ovProduct = this.store[STORE_OVERRIDES]?.[product.alias];

    if (!ovProduct) {
      return data;
    }

    if (!overrides.oldCampaign) {
      return data
    }

    const ovOptions = ovProduct[overrides.oldCampaign]?.options;

    if (!ovOptions) {
      return data;
    }

    for (const [variation, ovOption] of Object.entries(ovOptions)) {
      const option = product.options.get(variation);
      if (!ovOption) {
        product.options.delete(variation);
      } else {
        product.options.set(variation, { ...option, ...ovOption } as UnboundProductOptionData);
      }
    }

    return data;
  }

  private async applyTransformers(data: FetchedProduct) {
    const { product } = data;
    if (!product) {
      return data;
    }

    for (const option of product.options.values()) {
      if (this.store[STORE_TRANSFORMERS]?.buyLink) {
        option.buyLink = await this.store[STORE_TRANSFORMERS]?.buyLink(
          { buyLink: option.buyLink, product, option }
        );
      }

      if (this.store[STORE_TRANSFORMERS]?.trialLink && option.trialLinks) {
        for (const [name, trialLink] of Object.entries(option.trialLinks)) {
          option.trialLinks[name] = await this.store[STORE_TRANSFORMERS].trialLink(
            { name, trialLink, buyLink: option.buyLink, product, option }
          );
        }
      }
    }

    return data;
  }

  private async applyTrialLinks(data: FetchedProduct) {
    const { overrides, product } = data;

    if (!product) return data;

    const trProduct = this.store[STORE_TRIAL_LINKS]?.[product.alias];
    if (!trProduct) return data;

    const trOption = trProduct[overrides.newCampaign  || ""] || trProduct.default;
    if (!trOption) return data;

    for (const [key, link] of Object.entries(trOption)) {
      const option = product.options.get(key);
      if (option) {
        option.trialLinks = link;
      }
    }
    return data;
  }

  async getCampaign(id: string, campaign?: string): Promise<string | undefined> {
    if (!campaign && this.store[STORE_OVERRIDES]?.[id]?.default?.campaign) {
      return this.store[STORE_OVERRIDES]?.[id].default.campaign
    }

    if (campaign && this.store[STORE_OVERRIDES]?.[id]?.[campaign]?.campaign) {
      return this.store[STORE_OVERRIDES]?.[id]?.[campaign].campaign
    }

    return  await this.store[STORE_CAMPAIGN]({ id, campaign })
  }

  async adaptTo(param: { id: string }): Promise<{ id: string }>;
  async adaptTo(param: { id: string; devices: number; subscription: number }): Promise<{ id: string; devices: number; subscription: number }>;
  async adaptTo(param: { id: string; devices?: number; subscription?: number }): Promise<{ id: string; devices?: number; subscription?: number }> {
    const adaptor = await this.store[STORE_ADAPTER];
    return await adaptor.adaptTo(param);
  }

  protected abstract fetch(product: ProductSelector): Promise<FetchedProduct | undefined>;
}
